/**
 * info.yml, Phira's package manifest (W1-07b formats-package; contract formats/info/types.ts `ReadInfoYmlFn`,
 * `WriteInfoYmlFn`; formats-and-packaging.md P§2.3).
 *
 * Reading follows Phira's serde_yaml 0.9 typing rules, not YAML's own:
 *  - every missing key takes Phira's default (struct-level serde default: name "UK", difficulty 10.0, ...); an empty
 *    or comment-only document (or a bare `---`) is all defaults, without a diagnostic;
 *  - String fields accept any scalar as its text: a plain scalar is taken verbatim (`name: 10.50` is "10.50",
 *    `name: null` is "null"), a quoted or block scalar by its value; a mapping or a list is an error;
 *  - f32 fields need PLAIN scalars that Rust's float parser reads as a finite number (`15`, `007`, `1.5e0`, `.5`),
 *    or `.inf` / `-.inf` / `.nan`; `1e400` (infinite), hex / octal / binary integers and other text are errors
 *    ('yamlError'). i32 fields take decimal integers without leading zeros (`007` is text there) and `0x` / `0o` /
 *    `0b` integers. bool fields take true / false (also True, TRUE, ...). A quoted number or bool is an error Phira
 *    aborts the import on ('quotedNumber' / 'quotedBool'; the value is still used here);
 *  - Option fields accept `null`, `~` and an empty value; `tags` accepts an empty value as an empty list;
 *  - aliases (`charter: *name`) are read as the anchored node, keys included; an alias without its anchor is an error;
 *  - a known key written twice is an error ("duplicate field"); unknown keys are kept in `extra` (Phira drops them);
 *  - invalid YAML or a root that is not a mapping is 'yamlError' (defaults returned; `readInfoYmlDetailed` tells
 *    that the document as a whole could not be read).
 * Every diagnostic here is fatal for Phira: the first manifest it finds must parse, there is no fallback.
 *
 * Writing is hand-made (the yaml library would choose its own quoting): Phira's field order, strings always in
 * double quotes with YAML escapes, numbers and bools plain (f32 values as the shortest decimal that reads back as
 * the same f32, integral ones with ".0" as serde_yaml writes them), `null` for unset Option fields, the server
 * fields (id, uploader, created, updated, chartUpdated) only when present, unknown keys last (as YAML flow values),
 * "\n" line ends, no BOM. `readInfoYml(writeInfoYml(info))` gives `info` back without diagnostics.
 */

import { isAlias, isMap, isScalar, isSeq, parseDocument, Scalar } from 'yaml';
import type { Document, Node as YamlNode } from 'yaml';
import { floatLexeme } from '../json/shortest';
import { setOwn } from '../../core/util/json';
import type { Json } from '../../core/util/types';
import type { ChartFormatName, InfoYml, ManifestDiagnostic, ReadInfoYmlFn, WriteInfoYmlFn } from './types';

/** Phira's `ChartInfo::default()` (P§2.3). */
export const INFO_DEFAULTS: InfoYml = Object.freeze({
  id: null,
  uploader: null,
  name: 'UK',
  difficulty: 10,
  level: 'UK Lv.10',
  charter: 'UK',
  composer: 'UK',
  illustrator: 'UK',
  chart: 'chart.json',
  format: null,
  music: 'song.mp3',
  illustration: 'background.png',
  unlockVideo: null,
  previewStart: 0,
  previewEnd: null,
  aspectRatio: 16 / 9,
  backgroundDim: 0.6,
  lineLength: 6,
  offset: 0,
  tip: null,
  tags: Object.freeze([]),
  intro: '',
  holdPartialCover: false,
  noteUniformScale: false,
  forceAspectRatio: false,
  useRpe170Speed: null,
  useAttachUiFix: null,
  created: null,
  updated: null,
  chartUpdated: null,
  extra: Object.freeze([]),
});

/** A fresh copy of Phira's defaults. */
export function defaultInfo(): InfoYml {
  return { ...INFO_DEFAULTS };
}

type MutableInfo = { -readonly [K in keyof InfoYml]: InfoYml[K] };

/** Field kinds of ChartInfo as serde reads them. */
type FieldKind = 'string' | 'f32' | 'optF32' | 'bool' | 'optBool' | 'optI32' | 'optString' | 'format' | 'tags' | 'date';

/** Known keys in Phira's serialisation order (P§2.3). */
export const INFO_FIELDS: ReadonlyArray<readonly [keyof Omit<InfoYml, 'extra'>, FieldKind]> = [
  ['id', 'optI32'],
  ['uploader', 'optI32'],
  ['name', 'string'],
  ['difficulty', 'f32'],
  ['level', 'string'],
  ['charter', 'string'],
  ['composer', 'string'],
  ['illustrator', 'string'],
  ['chart', 'string'],
  ['format', 'format'],
  ['music', 'string'],
  ['illustration', 'string'],
  ['unlockVideo', 'optString'],
  ['previewStart', 'f32'],
  ['previewEnd', 'optF32'],
  ['aspectRatio', 'f32'],
  ['backgroundDim', 'f32'],
  ['lineLength', 'f32'],
  ['offset', 'f32'],
  ['tip', 'optString'],
  ['tags', 'tags'],
  ['intro', 'string'],
  ['holdPartialCover', 'bool'],
  ['noteUniformScale', 'bool'],
  ['forceAspectRatio', 'bool'],
  ['useRpe170Speed', 'optBool'],
  ['useAttachUiFix', 'optBool'],
  ['created', 'date'],
  ['updated', 'date'],
  ['chartUpdated', 'date'],
];

const FIELD_KIND: ReadonlyMap<string, FieldKind> = new Map(INFO_FIELDS);
const SERVER_FIELDS: ReadonlySet<string> = new Set(['id', 'uploader', 'created', 'updated', 'chartUpdated']);
const CHART_FORMATS: readonly ChartFormatName[] = ['rpe', 'pec', 'pgr', 'pbc'];

// ---------------------------------------------------------------------------------------------------------------
// Plain scalar resolution (serde_yaml 0.9)
// ---------------------------------------------------------------------------------------------------------------

/** Rust's `f64::from_str` grammar for finite decimals (its `inf` / `nan` words never give a finite number). */
const RUST_FLOAT_RE = /^[-+]?(?:\.[0-9]+|[0-9]+(?:\.[0-9]*)?)(?:[eE][-+]?[0-9]+)?$/;
const DEC_DIGITS_RE = /^-?[0-9]+$/;
const RADIX_DIGITS: Readonly<Record<'x' | 'o' | 'b', RegExp>> = {
  x: /^[0-9a-fA-F]+$/,
  o: /^[0-7]+$/,
  b: /^[01]+$/,
};
const RADIX: Readonly<Record<'x' | 'o' | 'b', number>> = { x: 16, o: 8, b: 2 };

/** YAML 1.2: leading zeros followed by digits make a string (`007`, `-01`), not a number. */
export function digitsButNotNumber(text: string): boolean {
  const s = text.startsWith('-') || text.startsWith('+') ? text.slice(1) : text;
  return s.length > 1 && s.startsWith('0') && /^[0-9]+$/.test(s.slice(1));
}

/** Value of the digits after a `0x` / `0o` / `0b` prefix (lower-case prefixes only, as in serde_yaml), or null. */
function radixInt(rest: string, kind: 'x' | 'o' | 'b'): number | null {
  if (!RADIX_DIGITS[kind].test(rest)) return null;
  const v = Number.parseInt(rest, RADIX[kind]);
  return Number.isFinite(v) ? v : null;
}

/**
 * Integer value of a plain scalar as serde_yaml reads it for a signed integer field, or null: an optional sign,
 * `0x` / `0o` / `0b` digits (also after '-'), or decimal digits without leading zeros.
 */
export function yamlPlainInt(text: string): number | null {
  let unpositive = text;
  if (text.startsWith('+')) {
    unpositive = text.slice(1);
    if (unpositive.startsWith('+') || unpositive.startsWith('-')) return null;
  }
  const negative = unpositive.startsWith('-');
  const body = negative ? unpositive.slice(1) : unpositive;
  for (const kind of ['x', 'o', 'b'] as const) {
    if (!body.startsWith(`0${kind}`)) continue;
    const v = radixInt(body.slice(2), kind);
    // `+ 0` turns -0 into 0 (an integer has no negative zero).
    if (v !== null) return (negative ? -v : v) + 0;
  }
  if (digitsButNotNumber(text)) return null;
  return DEC_DIGITS_RE.test(unpositive) ? Number(unpositive) + 0 : null;
}

/**
 * Number value of a plain scalar as serde_yaml reads it for a float field, or null: `.inf`, `+.inf`, `-.inf`,
 * `.nan` (case variants .Inf / .INF / .NaN / .NAN), else what Rust's float parser reads as a FINITE number (integers
 * in decimal, leading zeros allowed; `1e400` overflows and is refused; hex, octal and binary are refused).
 */
export function yamlPlainFloat(text: string): number | null {
  let unpositive = text;
  if (text.startsWith('+')) {
    unpositive = text.slice(1);
    if (unpositive.startsWith('+') || unpositive.startsWith('-')) return null;
  }
  if (unpositive === '.inf' || unpositive === '.Inf' || unpositive === '.INF') return Infinity;
  if (text === '-.inf' || text === '-.Inf' || text === '-.INF') return -Infinity;
  if (text === '.nan' || text === '.NaN' || text === '.NAN') return Number.NaN;
  if (!RUST_FLOAT_RE.test(unpositive)) return null;
  const v = Number(unpositive);
  return Number.isFinite(v) ? v : null;
}

/** Value of an untyped plain scalar (serde_yaml's resolution for a value of any type). */
function yamlPlainAny(text: string): Json {
  if (yamlPlainNull(text)) return null;
  const b = yamlPlainBool(text);
  if (b !== null) return b;
  const i = yamlPlainInt(text);
  if (i !== null && Number.isSafeInteger(i)) return i;
  if (!digitsButNotNumber(text)) {
    const f = yamlPlainFloat(text);
    if (f !== null && Number.isFinite(f)) return f;
  }
  return text;
}

/** Bool value of a plain scalar, or null. */
export function yamlPlainBool(text: string): boolean | null {
  if (text === 'true' || text === 'True' || text === 'TRUE') return true;
  if (text === 'false' || text === 'False' || text === 'FALSE') return false;
  return null;
}

/** `null`, `~` or an empty plain scalar. */
export function yamlPlainNull(text: string): boolean {
  return text === '' || text === '~' || text === 'null' || text === 'Null' || text === 'NULL';
}

/** RFC 3339 date-time (what chrono's DateTime<Utc> deserialises). */
const RFC3339_RE = /^\d{4}-\d{2}-\d{2}[Tt ]\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:[Zz]|[+-]\d{2}:\d{2})$/;

// ---------------------------------------------------------------------------------------------------------------
// Reader
// ---------------------------------------------------------------------------------------------------------------

interface ScalarView {
  readonly plain: boolean;
  /** Raw source text for plain scalars, the value for quoted / block scalars. */
  readonly text: string;
}

/** An empty value (`key:` or `? key`) reads as an empty plain scalar, as in serde_yaml. */
function scalarView(node: YamlNode | null): ScalarView | null {
  if (node === null) return { plain: true, text: '' };
  if (!isScalar(node)) return null;
  const plain = node.type === undefined || node.type === Scalar.PLAIN;
  const source = (node as { readonly source?: unknown }).source;
  const value: unknown = node.value;
  const text = plain && typeof source === 'string' ? source : typeof value === 'string' ? value : String(value);
  return { plain, text };
}

function isEmptyPlain(node: YamlNode | null): boolean {
  const v = scalarView(node);
  return v !== null && v.plain && v.text === '';
}

/** The anchored node of an alias (other nodes as they are); `undefined` for an alias without its anchor. */
type Resolve = (node: YamlNode | null) => YamlNode | null | undefined;

function resolverOf(doc: Document): Resolve {
  return (node) => {
    if (node === null || !isAlias(node)) return node;
    const target = node.resolve(doc) as YamlNode | undefined;
    return target === undefined || isAlias(target) ? undefined : target;
  };
}

/** JSON value of an unknown key (serde_yaml's resolution for plain scalars of any type). */
function yamlToJson(input: YamlNode | null, resolve: Resolve): Json {
  const node = resolve(input);
  if (node === null || node === undefined) return null;
  if (isScalar(node)) {
    const v = scalarView(node);
    if (v === null) return null;
    return v.plain ? yamlPlainAny(v.text) : v.text;
  }
  if (isSeq(node)) return node.items.map((item) => yamlToJson(item as YamlNode | null, resolve));
  if (isMap(node)) {
    const out: Record<string, Json> = {};
    for (const pair of node.items) {
      const keyNode = resolve(pair.key as YamlNode | null);
      const k = keyNode === undefined ? null : scalarView(keyNode);
      setOwn(out, k === null ? '' : k.text, yamlToJson(pair.value as YamlNode | null, resolve));
    }
    return out;
  }
  return null;
}

class InfoReader {
  readonly diagnostics: ManifestDiagnostic[] = [];
  readonly info: MutableInfo = { ...INFO_DEFAULTS };

  private readonly resolve: Resolve;

  constructor(resolve: Resolve) {
    this.resolve = resolve;
  }

  fatal(code: ManifestDiagnostic['code'], detail: string): void {
    this.diagnostics.push({ code, detail, phiraFatal: true });
  }

  /** Reads a plain number; quoted numbers are reported but used. */
  number(key: string, node: YamlNode | null, optional: boolean, integer: boolean): number | null | undefined {
    const v = scalarView(node);
    if (v === null) {
      this.fatal('yamlError', key);
      return undefined;
    }
    if (v.plain && optional && yamlPlainNull(v.text)) return null;
    const parse = integer ? yamlPlainInt : yamlPlainFloat;
    const n = parse(v.text.trim());
    if (!v.plain) {
      this.fatal('quotedNumber', key);
      return n ?? undefined;
    }
    if (n === null) {
      this.fatal('yamlError', key);
      return undefined;
    }
    if (integer && (n < -2147483648 || n > 2147483647)) {
      this.fatal('yamlError', key);
      return undefined;
    }
    return n;
  }

  bool(key: string, node: YamlNode | null, optional: boolean): boolean | null | undefined {
    const v = scalarView(node);
    if (v === null) {
      this.fatal('yamlError', key);
      return undefined;
    }
    if (v.plain && optional && yamlPlainNull(v.text)) return null;
    const b = yamlPlainBool(v.text.trim());
    if (!v.plain) {
      this.fatal('quotedBool', key);
      return b ?? undefined;
    }
    if (b === null) {
      this.fatal('yamlError', key);
      return undefined;
    }
    return b;
  }

  field(key: keyof Omit<InfoYml, 'extra'>, kind: FieldKind, input: YamlNode | null): void {
    const info = this.info as Record<string, unknown>;
    const node = this.resolve(input);
    if (node === undefined) {
      this.fatal('yamlError', key);
      return;
    }
    switch (kind) {
      case 'string': {
        const v = scalarView(node);
        if (v === null) this.fatal('yamlError', key);
        else info[key] = v.text;
        return;
      }
      case 'optString': {
        const v = scalarView(node);
        if (v === null) this.fatal('yamlError', key);
        else info[key] = v.plain && yamlPlainNull(v.text) ? null : v.text;
        return;
      }
      case 'f32':
      case 'optF32': {
        const n = this.number(key, node, kind === 'optF32', false);
        if (n === null && kind === 'f32') this.fatal('yamlError', key);
        else if (n !== undefined) info[key] = n;
        return;
      }
      case 'optI32': {
        const n = this.number(key, node, true, true);
        if (n !== undefined) info[key] = n;
        return;
      }
      case 'bool':
      case 'optBool': {
        const b = this.bool(key, node, kind === 'optBool');
        if (b === null && kind === 'bool') this.fatal('yamlError', key);
        else if (b !== undefined) info[key] = b;
        return;
      }
      case 'format': {
        const v = scalarView(node);
        if (v === null) {
          this.fatal('yamlError', key);
          return;
        }
        if (v.plain && yamlPlainNull(v.text)) {
          info[key] = null;
          return;
        }
        const f = CHART_FORMATS.find((x) => x === v.text);
        if (f === undefined) this.fatal('yamlError', key);
        else info[key] = f;
        return;
      }
      case 'tags': {
        // An empty value is an empty list (`tags:`); `~` and `null` are not.
        if (isEmptyPlain(node)) {
          info[key] = [];
          return;
        }
        if (node === null || !isSeq(node)) {
          this.fatal('yamlError', key);
          return;
        }
        const tags: string[] = [];
        for (const item of node.items) {
          const resolved = this.resolve(item as YamlNode | null);
          const v = resolved === undefined ? null : scalarView(resolved);
          if (v === null) {
            this.fatal('yamlError', key);
            return;
          }
          tags.push(v.text);
        }
        info[key] = tags;
        return;
      }
      case 'date': {
        const v = scalarView(node);
        if (v === null) {
          this.fatal('yamlError', key);
          return;
        }
        if (v.plain && yamlPlainNull(v.text)) {
          info[key] = null;
          return;
        }
        if (!RFC3339_RE.test(v.text)) this.fatal('yamlError', key);
        else info[key] = v.text;
        return;
      }
    }
  }
}

/** `readInfoYml` plus whether the document as a whole could be read (false: invalid YAML, root not a mapping). */
export interface InfoYmlRead {
  readonly info: InfoYml;
  readonly diagnostics: readonly ManifestDiagnostic[];
  /**
   * False when the document as a whole could not be read (invalid YAML, a root that is not a mapping): `info` then
   * holds only defaults. An empty document is readable (all defaults, as in Phira).
   */
  readonly readable: boolean;
}

/** Reads info.yml (see the module header) and tells whether the document as a whole was readable. */
export function readInfoYmlDetailed(text: string): InfoYmlRead {
  const doc = parseDocument(text, { schema: 'failsafe', uniqueKeys: false, prettyErrors: false });
  const resolve = resolverOf(doc);
  const r = new InfoReader(resolve);
  const firstError = doc.errors[0];
  if (firstError !== undefined) {
    r.fatal('yamlError', firstError.message.split('\n')[0] ?? '');
    return { info: defaultInfo(), diagnostics: r.diagnostics, readable: false };
  }
  const root = resolve(doc.contents);
  // No document, a comment-only one or a bare `---`: serde reads an empty mapping (every field its default).
  if (root === null || (root !== undefined && isEmptyPlain(root))) {
    return { info: defaultInfo(), diagnostics: [], readable: true };
  }
  if (root === undefined || !isMap(root)) {
    r.fatal('yamlError', 'root');
    return { info: defaultInfo(), diagnostics: r.diagnostics, readable: false };
  }
  const seen = new Set<string>();
  const extra: Array<readonly [string, Json]> = [];
  for (const pair of root.items) {
    const keyNode = resolve(pair.key as YamlNode | null);
    const k = keyNode === undefined ? null : scalarView(keyNode);
    if (k === null) {
      r.fatal('yamlError', 'key');
      continue;
    }
    const key = k.text;
    const value = pair.value as YamlNode | null;
    const kind = FIELD_KIND.get(key);
    if (kind === undefined) {
      const at = extra.findIndex(([x]) => x === key);
      if (at >= 0) extra.splice(at, 1);
      extra.push([key, yamlToJson(value, resolve)]);
      continue;
    }
    if (seen.has(key)) r.fatal('yamlError', `duplicate ${key}`);
    seen.add(key);
    r.field(key as keyof Omit<InfoYml, 'extra'>, kind, value);
  }
  r.info.extra = extra;
  return { info: r.info, diagnostics: r.diagnostics, readable: true };
}

export const readInfoYml: ReadInfoYmlFn = (text) => {
  const { info, diagnostics } = readInfoYmlDetailed(text);
  return { info, diagnostics };
};

// ---------------------------------------------------------------------------------------------------------------
// Writer
// ---------------------------------------------------------------------------------------------------------------

/** A YAML double-quoted scalar (escapes for `\`, `"`, control and line-break characters, BOM). */
export function yamlQuote(s: string): string {
  let out = '"';
  for (const ch of s) {
    const cp = ch.codePointAt(0) ?? 0;
    switch (cp) {
      case 0x5c:
        out += '\\\\';
        continue;
      case 0x22:
        out += '\\"';
        continue;
      case 0x0a:
        out += '\\n';
        continue;
      case 0x0d:
        out += '\\r';
        continue;
      case 0x09:
        out += '\\t';
        continue;
      case 0x00:
        out += '\\0';
        continue;
      default:
        break;
    }
    const isControl = cp < 0x20 || cp === 0x7f || (cp >= 0x80 && cp <= 0x9f);
    const isBreakOrBom = cp === 0x2028 || cp === 0x2029 || cp === 0xfeff;
    const isSurrogateOrNonChar = (cp >= 0xd800 && cp <= 0xdfff) || cp === 0xfffe || cp === 0xffff;
    if (isControl || isBreakOrBom || isSurrogateOrNonChar) {
      out += `\\u${cp.toString(16).padStart(4, '0')}`;
      continue;
    }
    out += ch;
  }
  return `${out}"`;
}

/** f32 field: shortest decimal reading back as the same f32, integral values with ".0" (serde_yaml style). */
export function yamlF32(v: number): string {
  if (Number.isNaN(v)) return '.nan';
  if (!Number.isFinite(v)) return v > 0 ? '.inf' : '-.inf';
  const f = Math.fround(v);
  if (!Number.isFinite(f)) return f > 0 ? '.inf' : '-.inf';
  return floatLexeme(f, 'f32', 'rpe');
}

/** A JSON value in YAML flow syntax (unknown keys written back). */
function yamlFlow(v: Json): string {
  if (v === null) return 'null';
  if (typeof v === 'boolean') return v ? 'true' : 'false';
  if (typeof v === 'number') return Number.isFinite(v) ? String(v) : 'null';
  if (typeof v === 'string') return yamlQuote(v);
  if (Array.isArray(v)) return `[${(v as readonly Json[]).map((x) => yamlFlow(x)).join(', ')}]`;
  const entries = Object.entries(v as { readonly [k: string]: Json });
  return `{${entries.map(([k, x]) => `${yamlQuote(k)}: ${yamlFlow(x)}`).join(', ')}}`;
}

const PLAIN_KEY_RE = /^[A-Za-z_][A-Za-z0-9_]*$/;

export const writeInfoYml: WriteInfoYmlFn = (info) => {
  const lines: string[] = [];
  const rec = info as unknown as Readonly<Record<string, unknown>>;
  for (const [key, kind] of INFO_FIELDS) {
    const v = rec[key];
    if (SERVER_FIELDS.has(key) && (v === null || v === undefined)) continue;
    switch (kind) {
      case 'string':
        lines.push(`${key}: ${yamlQuote(typeof v === 'string' ? v : '')}`);
        break;
      case 'optString':
      case 'date':
      case 'format':
        lines.push(`${key}: ${typeof v === 'string' ? yamlQuote(v) : 'null'}`);
        break;
      case 'f32':
        lines.push(`${key}: ${yamlF32(typeof v === 'number' ? v : 0)}`);
        break;
      case 'optF32':
        lines.push(`${key}: ${typeof v === 'number' ? yamlF32(v) : 'null'}`);
        break;
      case 'optI32':
        lines.push(`${key}: ${typeof v === 'number' && Number.isInteger(v) ? String(v) : 'null'}`);
        break;
      case 'bool':
        lines.push(`${key}: ${v === true ? 'true' : 'false'}`);
        break;
      case 'optBool':
        lines.push(`${key}: ${typeof v === 'boolean' ? String(v) : 'null'}`);
        break;
      case 'tags': {
        const tags = Array.isArray(v)
          ? (v as readonly unknown[]).filter((t): t is string => typeof t === 'string')
          : [];
        if (tags.length === 0) lines.push(`${key}: []`);
        else {
          lines.push(`${key}:`);
          for (const t of tags) lines.push(`  - ${yamlQuote(t)}`);
        }
        break;
      }
    }
  }
  // Unknown keys: the last occurrence of a key wins (as a YAML reader keeps it), written at its own position.
  const lastIndex = new Map<string, number>();
  info.extra.forEach(([key], i) => lastIndex.set(key, i));
  const extraLines: string[] = [];
  info.extra.forEach(([key, value], i) => {
    if (FIELD_KIND.has(key) || lastIndex.get(key) !== i) return;
    extraLines.push(`${PLAIN_KEY_RE.test(key) ? key : yamlQuote(key)}: ${yamlFlow(value)}`);
  });
  return `${[...lines, ...extraLines].join('\n')}\n`;
};
